import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, type OnInit, signal, type WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MaloupeLocale } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { DiagnosticHttpService } from ':core/http-services/diagnostic.http-service';
import { HubspotFormEmbedService } from ':core/services/hubspot-form-embed.service';
import { environment } from ':environments/environment';
import { COACHING_SESSIONS_PLANNER_URL } from ':shared/constants';
import { Icon } from ':shared/enums';
import { ImagePathResolverPipe } from ':shared/pipes';

@Component({
    selector: 'app-diagnostic-information-form',
    imports: [NgTemplateOutlet, MatButtonModule, TranslateModule, ImagePathResolverPipe],
    templateUrl: './diagnostic-information-form.component.html',
    styleUrl: './diagnostic-information-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticInformationFormComponent implements OnInit {
    readonly diagnostics = input.required<Diagnostic[]>();

    readonly diagnosticSent: WritableSignal<boolean> = signal(false);

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _translateService = inject(TranslateService);
    private readonly _diagnosticHttpService = inject(DiagnosticHttpService);
    private readonly _hubspotFormEmbedService = inject(HubspotFormEmbedService);
    private readonly _destroyRef = inject(DestroyRef);

    readonly Icon = Icon;
    readonly hubspotFormContainerId = 'hubspot-diagnostic-information-form';

    ngOnInit(): void {
        this._activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((queryParams) => {
            const diagnosticIds = this.diagnostics().map((diagnostic) => diagnostic.id);
            this._diagnosticHttpService
                .getDiagnosticShareUrl$(diagnosticIds)
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe({
                    next: (publicDiagnosticUrl) => {
                        const language = this._translateService.currentLang as MaloupeLocale;
                        const values: Record<string, string> = {
                            diagnostic_results: publicDiagnosticUrl,
                            zip: this.diagnostics()[0]?.restaurant.address.postalCode ?? '',
                            utm_source: queryParams.get('utm_source') ?? '',
                            utm_medium: queryParams.get('utm_medium') ?? '',
                            utm_campaign: queryParams.get('utm_campaign') ?? '',
                            utm_content: 'AppMaloupe',
                            language: language.toUpperCase() === 'FR' ? 'French' : 'English',
                        };
                        this._createHubspotForm(language, values);
                    },
                    error: (error) => {
                        console.error('Failed to fetch diagnostic share URL', error);
                    },
                });
        });
    }

    redirectToCoachingSessionsPlanner(): void {
        window.open(COACHING_SESSIONS_PLANNER_URL, '_blank');
    }

    private _createHubspotForm(language: MaloupeLocale, values: Record<string, string>): void {
        const { HUBSPOT } = environment;
        const formId = language === MaloupeLocale.FR ? HUBSPOT.formIdFr : HUBSPOT.formIdEn;
        this._hubspotFormEmbedService
            .createForm({
                region: HUBSPOT.region,
                portalId: HUBSPOT.portalId,
                formId,
                targetSelector: `#${this.hubspotFormContainerId}`,
                values,
                onFormSubmitted: () => this.diagnosticSent.set(true),
            })
            .catch((error) => {
                console.error('Failed to initialize HubSpot embed form', error);
            });
    }
}
